/**
 * DeliveryScheduleCard - Global delivery schedule management
 * Shows next delivery date with actions that affect ALL subscriptions
 */
import { Box, Typography, Stack, Alert } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { QuizStyledCard } from "./shared/QuizStyledCard";
import { QUIZ } from "@/theme/quizTokens";

type AddressInfo = {
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  zip?: string;
  country?: string;
};

type DeliveryScheduleCardProps = {
  nextDeliveryDate: string | null;
  nextChargeDate: string | null;
  isCharged?: boolean;
  cutoffHours?: number;
  address?: AddressInfo | null;
  onChangeDeliveryDate?: () => void;
  onSkipDelivery?: () => void;
};

// Parse the calendar-day part and format pinned to UTC so the day never
// shifts with the viewer's timezone (dates are store-local calendar days).
function formatDeliveryDate(dateStr: string | null): string {
  if (!dateStr) return "Not scheduled";
  const date = new Date(dateStr.slice(0, 10) + "T00:00:00Z");
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return "";
  const date = new Date(dateStr.slice(0, 10) + "T00:00:00Z");
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

function formatAddress(address: AddressInfo | null | undefined): string {
  if (!address) return "";
  const parts = [
    address.address1,
    address.city,
    address.zip,
  ].filter(Boolean);
  return parts.join(", ");
}

function formatCutoffMessage(cutoffHours?: number): string {
  if (!cutoffHours || cutoffHours <= 0) return "Too late to change";
  if (cutoffHours < 24) return `Less than ${cutoffHours}h before delivery — changes locked`;
  const days = Math.floor(cutoffHours / 24);
  const remainingHours = cutoffHours % 24;
  if (remainingHours === 0) {
    return `Less than ${days} day${days > 1 ? "s" : ""} before delivery — changes locked`;
  }
  return `Less than ${days}d ${remainingHours}h before delivery — changes locked`;
}

export function DeliveryScheduleCard({
  nextDeliveryDate,
  nextChargeDate,
  isCharged = false,
  cutoffHours,
  address,
  onChangeDeliveryDate,
  onSkipDelivery,
}: DeliveryScheduleCardProps) {
  const cutoffMessage = formatCutoffMessage(cutoffHours);

  const actions = [
    {
      label: "Change delivery date",
      description: isCharged
        ? cutoffMessage
        : "Select a new date for all subscriptions",
      icon: CalendarTodayIcon,
      onClick: onChangeDeliveryDate,
      color: "#1A57D3",
      bgColor: "#E6F0FF",
      disabled: isCharged,
    },
    {
      label: "Skip next delivery",
      description: isCharged
        ? cutoffMessage
        : "Skip this delivery for all subscriptions",
      icon: SkipNextIcon,
      onClick: onSkipDelivery,
      color: "#FF9800",
      bgColor: "#FFF3E0",
      disabled: isCharged,
    },
  ];

  const addressText = formatAddress(address);

  return (
    <QuizStyledCard variant="white">
      {/* Address Header */}
      {addressText && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            pb: 2,
            borderBottom: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <LocationOnIcon sx={{ fontSize: 18, color: QUIZ.color.brown }} />
          <Typography
            sx={{
              fontSize: { xs: 13, md: 14 },
              fontWeight: 600,
              color: QUIZ.color.brown,
            }}
          >
            Delivering to: {addressText}
          </Typography>
        </Box>
      )}

      {/* Charged Notice */}
      {isCharged && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{
            mb: 2,
            borderRadius: "12px",
            "& .MuiAlert-icon": { alignItems: "center" },
          }}
        >
          {cutoffMessage}. You can no longer change the delivery date or skip
          this delivery.
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2.5, md: 4 },
          alignItems: { xs: "stretch", md: "center" },
        }}
      >
        {/* Next Delivery Info */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                background: isCharged
                  ? "linear-gradient(135deg, #E6F0FF 0%, #C9DFFF 100%)"
                  : "linear-gradient(135deg, #E5FFEA 0%, #C8F5CE 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LocalShippingIcon
                sx={{ fontSize: 24, color: isCharged ? "#1A57D3" : "#2E7D32" }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: 12, md: 13 },
                  fontWeight: 600,
                  color: isCharged ? "#1A57D3" : "#2E7D32",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {isCharged ? "Order Confirmed" : "Next Delivery"}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 18, md: 22 },
                  fontWeight: 800,
                  color: QUIZ.color.brown,
                  lineHeight: 1.2,
                }}
              >
                {formatDeliveryDate(nextDeliveryDate)}
              </Typography>
            </Box>
          </Box>

          {nextChargeDate && !isCharged && (
            <Typography
              sx={{
                fontSize: { xs: 12, md: 13 },
                color: QUIZ.color.brown,
                opacity: 0.6,
                ml: 7.5,
              }}
            >
              Charges on {formatShortDate(nextChargeDate)}
            </Typography>
          )}
        </Box>

        {/* Action Buttons */}
        {!isCharged && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ flexShrink: 0 }}
          >
            {actions.map((action) => {
              const IconComponent = action.icon;
              const isDisabled = action.disabled || !action.onClick;
              return (
                <Box
                  key={action.label}
                  onClick={isDisabled ? undefined : action.onClick}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: { xs: 2, md: 2.5 },
                    py: { xs: 1.5, md: 1.75 },
                    borderRadius: "12px",
                    border: "1px solid rgba(0,0,0,.08)",
                    background: QUIZ.color.cream,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: isDisabled ? 0.5 : 1,
                    transition: "all 150ms ease",
                    minWidth: { xs: "auto", sm: 200 },
                    "&:hover": !isDisabled
                      ? {
                          background: action.bgColor,
                          borderColor: action.color,
                          transform: "translateY(-1px)",
                          boxShadow: `0 4px 12px ${action.color}20`,
                        }
                      : {},
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    background: action.bgColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <IconComponent sx={{ fontSize: 20, color: action.color }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: 13, md: 14 },
                      fontWeight: 700,
                      color: QUIZ.color.brown,
                    }}
                    noWrap
                  >
                    {action.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 11, md: 12 },
                      color: QUIZ.color.brown,
                      opacity: 0.6,
                      display: { xs: "none", sm: "block" },
                    }}
                    noWrap
                  >
                    {action.description}
                  </Typography>
                </Box>
                <ChevronRightIcon
                  sx={{ fontSize: 20, color: QUIZ.color.brown, opacity: 0.3 }}
                />
              </Box>
            );
          })}
        </Stack>
        )}
      </Box>
    </QuizStyledCard>
  );
}
